/* eslint-disable no-console */
import _ from 'lodash';
import { batch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { fetchDataSource } from './data-source.actions';
import { clearFormDesigner, updateFormModel } from './form-manager.actions';
import { setSubmitInitiated } from '../../actions/CertificationActions';
import { setOpenDetailFormTabEntityDisplay } from '../../actions/EntityDisplayActions';
import { clearErrors, handleErrors } from '../../actions/ErrorActions';
import { setCustomLongLoadPageMessage } from '../../actions/LongLoadPageActions';
import { saveRecord } from '../../actions/MasterSetupPageActions';
import { clearFormErrorsAndWarnings } from '../../actions/NotificationDialogActions';
import { lockPanelLayoutConfig, setPanelLayoutConfig } from '../../actions/PanelLayoutActions';
import { clearSuccesses } from '../../actions/SuccessActions';
import { addToast } from '../../actions/ToastActions';
import { addWarning, clearWarnings, updateAllWarnings } from '../../actions/WarningActions';
import {
  ON_SUBMIT_START,
  startAutoChecking,
  stopAutoChecking,
} from '../../actions/WorkflowActions';
import { getHighVolumeData } from '../../api/high-volume-data';
import { getSavedSearchResultsForAutocomplete } from '../../api/saved-searches';
import { getAISummary } from '../../api/summary';
import { TOAST_TYPE } from '../../constants/NotificationConstants';
import { PANEL_CONFIGURATIONS } from '../../constants/PanelLayoutConstants';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { isFalse, isTrue } from '../../utils/commonUtils';
import {
  logDefaultValueEvaluationResult,
  logInteractionRulesEvaluationResult,
  logSetValueEvaluationResult,
  startTimer,
  SUBMIT_TIMER,
} from '../../utils/log';
import { isEmpty, set } from '../../utils/ramdaUtils';
import { DEFAULT_VIEW_TYPE } from '../constants/editor.constants';
import {
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../constants/form-manager.constants';
import {
  BUTTONS_CAUSE_VALIDATION,
  COPY_ROW_SKIP_ATTRIBUTES,
  FORM_CHANGES_TABLE_TYPES,
  INTERACTION_RULES_SUPPORTED_TYPES,
  RUNTIME_FORMS,
} from '../constants/runtime.constants';
import { getSelectedSubGrids, getUserMadeInteraction } from '../selectors/common.selectors';
import { getEvaluatedExpression, getSsContext } from '../selectors/form-manager.selectors';
import { getVirtualRows } from '../selectors/ssGrid.selectors';
import {
  changeModelId,
  checkForBooleanDataType,
  checkForLeftPanelWithErrors,
  clearTableByUniqueIdentifier,
  cloneAndOmitDeep,
  getAllEntitiesByEntityName,
  getAutoCompleteOptionLabel,
  getControlsByFormChanges,
  getPilgrimFormModel,
  getReducedFields,
  getSetOpenErrorsTabAction,
  getZeroBaseGridArrIndex,
  handleNewSelectedSubGrid,
  isCertificationPage,
  processCopiedRow,
  runUpdateRules,
  runValidationRules,
  setFieldValue,
} from '../utils/actions/runtime.actions.utils';
import { createChangesObj } from '../utils/controls/ssZoom.utils';
import { evaluateConditions } from '../utils/evaluation.utils';
import {
  findClosestParentIdByType,
  getDataPath,
  preprocessEntityData,
  translate,
} from '../utils/form-manager.utils';
import {
  createExtraPrompt,
  filterEntityData,
  getAutoCompleteRequestParams,
  getControlsWithIndexes,
  getDataForController,
  getTextViewByTextBoxId,
  getValueWithDataType,
  getVisibleZoomByTextBoxId,
  validateControls,
} from '../utils/runtime.utils';
import {
  clearValidationStorage,
  setAutoCompleteErrorsCache,
} from '../utils/validationStorage.utils';

export const UPDATE_ORIGINAL_DATA = 'UPDATE_ORIGINAL_DATA';
export const UPDATE_DATA_SUCCESS = 'UPDATE_DATA_SUCCESS';
export const BULK_UPDATE_FORM_DATA = 'BULK_UPDATE_FORM_DATA';
export const UPDATE_ENTITY_DATA = 'UPDATE_ENTITY_DATA';
export const DELETE_FORM_DATA_ROW = 'DELETE_FORM_DATA_ROW';
export const ADD_NEW_ROW = 'ADD_NEW_ROW';
export const CLEAR_TABLE = 'CLEAR_TABLE';
export const BULK_ADDING_NEW_ROWS = 'BULK_ADDING_NEW_ROWS';
export const SET_USER_MADE_INTERACTION = 'SET_USER_MADE_INTERACTION';
export const SET_ACTIVE_CONTAINERS = 'SET_ACTIVE_CONTAINERS';
export const UPDATE_SELECTED_SUB_GRIDS = 'UPDATE_SELECTED_SUB_GRIDS';
export const UPDATE_ENTITY_ROWS = 'UPDATE_ENTITY_ROWS';
export const SET_NUMBERING_SCHEME = 'SET_NUMBERING_SCHEME';
export const SET_SHOULD_EXECUTE_SET_DEFAULT_ACTIONS = 'SET_SHOULD_EXECUTE_SET_DEFAULT_ACTIONS';
export const SET_SHOULD_SKIP_FORM_RULES_EVALUATION = 'SET_SHOULD_SKIP_FORM_RULES_EVALUATION';
export const IS_MODULE_LICENSED = 'IS_MODULE_LICENSED';
export const SET_DATE_ADD_BUSINESS_DAYS = 'SET_DATE_ADD_BUSINESS_DAYS';
export const SET_IS_FORM_CHANGES_MODE = 'SET_IS_FORM_CHANGES_MODE';
export const SET_FORM_CHANGES_TABLE_ROWS = 'SET_FORM_CHANGES_TABLE_ROWS';
export const SET_IS_FORM_CHANGES_TABLE_LOADING = 'SET_IS_FORM_CHANGES_TABLE_LOADING';

export const updateOriginalData = (formData, formKey) => ({
  type: UPDATE_ORIGINAL_DATA,
  formKey,
  payload: {
    formData,
  },
});

export const updateDataSuccess = (formData, formKey) => ({
  type: UPDATE_DATA_SUCCESS,
  formKey,
  payload: {
    formData,
  },
});

export const setNumberingScheme = (formKey, numberingScheme) => ({
  type: SET_NUMBERING_SCHEME,
  formKey,
  payload: { numberingScheme },
});

export const setModuleIsLicensed = (moduleName, formKey, moduleLicensed) => ({
  type: IS_MODULE_LICENSED,
  formKey,
  payload: { moduleLicensed: { [moduleName]: moduleLicensed } },
});

export const setDateAddBusinessDays = (formKey, identifier, businessDaysDate) => ({
  type: SET_DATE_ADD_BUSINESS_DAYS,
  formKey,
  payload: { identifier, businessDaysDate },
});

export const setShouldExecuteSetDefaultActions = (flag, formKey) => ({
  type: SET_SHOULD_EXECUTE_SET_DEFAULT_ACTIONS,
  formKey,
  payload: { flag },
});

export const setShouldSkipFormRulesEvaluation = (flag, formKey) => ({
  type: SET_SHOULD_SKIP_FORM_RULES_EVALUATION,
  formKey,
  payload: { flag },
});

export const applyDefaultValue = ({ value, id, index, formKey }) => (dispatch, getState) => {
  const state = getState();

  if (isEmpty(state.formDesigner)) {
    return;
  }

  const { entityDefinition, formModel } = state.formDesigner[formKey];
  const { entityName, fieldName, defaultValue } = formModel[id];
  const entityType = Object.keys(entityDefinition)[0];
  const ctrValue = getValueWithDataType(value, entityDefinition, entityType, entityName, fieldName);
  const changeToApply = [{ value: ctrValue, entityName, fieldName, index }];

  logDefaultValueEvaluationResult({ ...changeToApply[0], formula: defaultValue, id });

  batch(() => {
    dispatch(setShouldExecuteSetDefaultActions(true, formKey));
    dispatch(bulkUpdateFormData(changeToApply, formKey));
  });
};

export const bulkUpdateFormData = (changesArray, formKey) => ({
  type: BULK_UPDATE_FORM_DATA,
  formKey,
  payload: {
    changesArray,
  },
});

export const updateEntityData = (newEntityData, formKey) => ({
  type: UPDATE_ENTITY_DATA,
  formKey,
  payload: {
    newEntityData,
  },
});

export const applyFormRules = ({ formKey, formRules, formsAreMountedRef }) => {
  return (dispatch, getState) => {
    const state = getState();

    if (isEmpty(state.formDesigner) || !formRules.length) {
      return;
    }

    const { shouldSkipFormRulesEvaluation } = state.formDesigner[formKey];

    if (shouldSkipFormRulesEvaluation) {
      if (formsAreMountedRef?.current) {
        formsAreMountedRef.current = false;
      }
      return;
    }

    const isDataChangedRef = { flag: false };
    let ssContext = getSsContext(state, { formKey });
    ssContext = { ...ssContext, data: _.cloneDeep(ssContext.data) };

    runUpdateRules({
      controls: [],
      ssContext,
      formRules,
      isDataChangedRef,
    });

    if (isDataChangedRef.flag) {
      batch(() => {
        dispatch(setShouldExecuteSetDefaultActions(false, formKey));
        dispatch(updateDataSuccess(ssContext.data, formKey));
      });
    }
  };
};

export const updateControlValueAndApplyControlRules = (
  value,
  { entityName, fieldName, index, formKey, id, invokeFormRules }
) => (dispatch, getState) => {
  const state = getState();

  let ssContext = getSsContext(state, { formKey });
  ssContext = { ...ssContext, data: _.cloneDeep(ssContext.data) };

  const { formModel, entityDefinition, data } = ssContext;
  const { formRules } = state.formDesigner[formKey];
  const control = formModel[id];
  const ctrValue = checkForBooleanDataType({ value, entityDefinition, entityName, fieldName });

  setFieldValue(data, entityDefinition, {
    fieldName,
    entityName,
    index,
    value: ctrValue,
  });

  runUpdateRules({
    controls: [[control, index]],
    ssContext,
    formRules: invokeFormRules ? formRules : [],
  });

  batch(() => {
    dispatch(setShouldExecuteSetDefaultActions(true, formKey));
    dispatch(updateDataSuccess(ssContext.data, formKey));
  });
};

export const bulkUpdateFormDataWithRules = (changesToApply = [], formKey) => (
  dispatch,
  getState
) => {
  const state = getState();

  let ssContext = getSsContext(state, { formKey });
  ssContext = { ...ssContext, data: _.cloneDeep(ssContext.data) };

  const { formModel, entityDefinition, data } = ssContext;
  const { formRules } = state.formDesigner[formKey];
  const controlsToApplyUpdate = getControlsByFormChanges(changesToApply, formModel);
  const invokeFormRules = controlsToApplyUpdate.some(([control]) =>
    isTrue(control.invokeFormRules)
  );

  changesToApply.forEach(({ fieldName, entityName, index, value }) => {
    setFieldValue(data, entityDefinition, {
      fieldName,
      entityName,
      index,
      value,
    });
  });

  runUpdateRules({
    controls: controlsToApplyUpdate,
    ssContext,
    formRules: invokeFormRules ? formRules : [],
  });

  batch(() => {
    dispatch(setShouldExecuteSetDefaultActions(true, formKey));
    dispatch(updateDataSuccess(ssContext.data, formKey));
  });
};

/**
 * value is empty -> clear all the values from the mapping
 * value is invalid -> clear all the values from the mapping, but leave the text box value as it is
 */
export const handleEmptyOrInvalidAutoCompleteValue = ({
  value,
  index,
  dataMappingList,
  formKey,
  textBoxFieldName,
}) => (dispatch) => {
  const mappings = dataMappingList.map((mapping) => mapping.updateTo.split('.'));

  const changesArray = mappings.map(([entityName, fieldName]) => ({
    entityName,
    fieldName,
    index,
    value: textBoxFieldName === fieldName ? value : '',
  }));

  if (changesArray.length > 0) {
    dispatch(bulkUpdateFormDataWithRules(changesArray, formKey));
  }
};

export const autoCompleteControlValue = ({
  value,
  instanceId,
  label,
  formKey,
  rowId,
  custom = false,
}) => {
  return async (dispatch, getState) => {
    const reduxState = getState();
    const translations = reduxState.translations;
    const { formModel, entityDefinition } = getState().formDesigner[formKey];
    const id = instanceId.split('#')[0];
    const index = JSON.parse(instanceId.split('#')[1]);
    const { entityName, fieldName } = formModel[id];
    const ssContext = getSsContext(reduxState, { index, formKey, entityName });
    const ssZoom = getVisibleZoomByTextBoxId(formModel, id, ssContext);
    const ssTextView = getTextViewByTextBoxId(formModel, id) ?? {};
    const dataMappingList = ssZoom.dataMappingList;
    const { enforceSelection } = formModel[id];

    if (!value || custom) {
      dispatch(
        handleEmptyOrInvalidAutoCompleteValue({
          value,
          index,
          dataMappingList,
          formKey,
          textBoxFieldName: fieldName,
        })
      );
      return;
    }

    const requestParams = getAutoCompleteRequestParams({
      value,
      instanceId,
      entityName,
      textBoxFieldName: fieldName,
      textViewFieldName: ssTextView.fieldName,
      formKey,
      reduxState,
    });

    const response = await getSavedSearchResultsForAutocomplete(requestParams);
    const dataRows = response?.SearchData?.rows ?? [];
    const dataColumns = response?.SearchData?.columns ?? [];

    if (!dataRows.length) {
      batch(() => {
        dispatch(
          handleEmptyOrInvalidAutoCompleteValue({
            value,
            index,
            dataMappingList,
            formKey,
            textBoxFieldName: fieldName,
          })
        );

        if (isFalse(enforceSelection)) {
          return;
        }

        dispatch(
          addWarning({
            instanceId,
            source: WARNING_SOURCES.AUTOCOMPLETE,
            message: `${label} ${translate(translations, 'is invalid')}.`,
          })
        );

        setAutoCompleteErrorsCache(rowId, id, value);
      });
      return;
    }

    let selectedDataRow = dataRows[0];

    if (!selectedDataRow && isFalse(enforceSelection)) {
      const resolverFieldMapping = dataMappingList.find(
        (mapping) => mapping.updateTo === `${entityName}.${fieldName}`
      );
      const resolverField = resolverFieldMapping.updateFrom;
      selectedDataRow = { [resolverField]: value };
    }

    const changesToApply = dataMappingList.reduce(
      createChangesObj(index, selectedDataRow, entityDefinition, dataColumns),
      []
    );

    if (changesToApply.length > 0) {
      dispatch(bulkUpdateFormDataWithRules(changesToApply, formKey));
    }
  };
};

export const getAutoCompleteOptions = (value, entityName, fieldName, instanceId, formKey) => async (
  _,
  getState
) => {
  const reduxState = getState();
  const { formModel, entityDefinition } = reduxState.formDesigner[formKey];
  const id = instanceId.split('#')[0];
  const ssTextView = getTextViewByTextBoxId(formModel, id) ?? {};

  const requestParams = getAutoCompleteRequestParams({
    value,
    instanceId,
    entityName,
    textBoxFieldName: fieldName,
    textViewFieldName: ssTextView.fieldName,
    formKey,
    reduxState,
  });

  const response = await getSavedSearchResultsForAutocomplete(requestParams);
  const dataRows = response?.SearchData?.rows ?? [];
  const dataColumns = response?.SearchData?.columns ?? [];

  if (!dataRows.length) {
    return [];
  }

  const index = JSON.parse(instanceId.split('#')[1]);
  const ssContext = getSsContext(reduxState, { index, formKey, entityName });
  const ssZoom = getVisibleZoomByTextBoxId(formModel, id, ssContext);
  const dataMappingList = ssZoom.dataMappingList;

  const autoCompleteOptions = dataRows.reduce((acc, curr) => {
    const changesToApply = dataMappingList.reduce(
      createChangesObj(index, curr, entityDefinition, dataColumns),
      []
    );

    const textBoxChange = changesToApply.find((change) => change.fieldName === fieldName);
    // get only textView value
    const textViewChange = changesToApply.filter(
      (change) => change.fieldName === ssTextView.fieldName
    );

    const label = getAutoCompleteOptionLabel(textBoxChange, textViewChange);

    acc.push({ label, changesToApply, value: textBoxChange.value });
    return acc;
  }, []);

  return autoCompleteOptions;
};

export const deleteFormDataRow = ({ rowIndex, entityName, index, formKey }) => ({
  type: DELETE_FORM_DATA_ROW,
  formKey,
  payload: {
    rowIndex,
    entityName,
    index,
  },
});

export const addRowAction = (entityName, index, initialRowData = {}, formKey) => ({
  type: ADD_NEW_ROW,
  formKey,
  payload: {
    entityName,
    index,
    initialRowData,
  },
});

export const addRow = ({ entityName, index, initialRowData = {}, formKey, isParentlessTable }) => (
  dispatch,
  getState
) => {
  if (!isParentlessTable) {
    dispatch(addRowAction(entityName, index, initialRowData, formKey));
    return;
  }

  batch(() => {
    const { entityDefinition, data } = getState().formDesigner[formKey];
    const entities = getAllEntitiesByEntityName(entityName).slice(1);

    for (const entity of entities) {
      const gridArrIndex = getZeroBaseGridArrIndex(entity);
      const path = getDataPath(entity, gridArrIndex, entityDefinition);
      const entityRows = _.get(data.entityDataStore.entityData, path.slice(0, -1));

      if (entityRows?.length && entity !== entityName) {
        continue;
      }

      dispatch(addRowAction(entity, gridArrIndex, { ...initialRowData }, formKey));
    }
  });
};

export function clearTable(entityName, index, clearedRows, formKey) {
  return {
    type: CLEAR_TABLE,
    formKey,
    payload: {
      entityName,
      index,
      clearedRows,
    },
  };
}

export const evaluateInitialRowsProperty = ({ selectorProps, formKey, initialRowsCount }) => (
  dispatch,
  getState
) => {
  const { formDesigner } = getState();

  if (!formDesigner[formKey]) {
    return;
  }

  const { data, entityDefinition } = formDesigner[formKey];
  const rows = getVirtualRows(getState(), { ...selectorProps, formKey });
  const rowsLength = rows.filter((row) => !row.isFilteredOut).length;

  if (rowsLength) {
    return;
  }

  if (selectorProps.entityName === 'V_ENU_NONPERSIS_USER_DATA') {
    const entityType = Object.keys(entityDefinition)[0];

    const updatedData = {
      ...data,
      entityDataStore: {
        ...data.entityDataStore,
        entityData: {
          ...data.entityDataStore.entityData,
          [entityType]: {
            ...data.entityDataStore.entityData[entityType],
            V_ENU_NONPERSIS_USER_DATA: {},
          },
        },
      },
    };

    dispatch(updateDataSuccess(updatedData, formKey));
    return;
  }

  const newRows = Array(initialRowsCount).fill({});

  batch(() => {
    dispatch(bulkAddingNewRows({ ...selectorProps, newRows, formKey }));
    dispatch(setShouldSkipFormRulesEvaluation(true, formKey));
  });
};

export const bulkAddingNewRowsAction = ({ entityName, index, newRows, insertMode, formKey }) => ({
  type: BULK_ADDING_NEW_ROWS,
  formKey,
  payload: {
    entityName,
    index,
    newRows,
    insertMode,
  },
});

export const bulkAddingNewRows = ({
  entityName,
  index,
  isParentlessTable,
  newRows,
  insertMode,
  formKey,
}) => (dispatch, getState) => {
  if (!isParentlessTable) {
    dispatch(bulkAddingNewRowsAction({ entityName, index, newRows, insertMode, formKey }));
    return;
  }

  batch(() => {
    const { entityDefinition, data } = getState().formDesigner[formKey];
    const entities = getAllEntitiesByEntityName(entityName).slice(1);

    for (const entity of entities) {
      const gridArrIndex = getZeroBaseGridArrIndex(entity);
      const path = getDataPath(entity, gridArrIndex, entityDefinition);
      const entityRows = _.get(data.entityDataStore.entityData, path.slice(0, -1));

      if (entity !== entityName) {
        if (!entityRows?.length) {
          dispatch(addRowAction(entity, gridArrIndex, {}, formKey));
        }
        continue;
      }

      dispatch(bulkAddingNewRowsAction({ entityName, index: gridArrIndex, newRows, formKey }));
    }
  });
};

export const copyRow = ({ newRowId, rowIndex, gridIndex, gridId, formKey, isParentlessTable }) => (
  dispatch,
  getState
) => {
  const { data, formModel, entityDefinition } = getState().formDesigner[formKey];
  const { entityName, excludedColumnsMappingList = [] } = formModel[gridId];

  const gridArrIndex = [...gridIndex];
  gridArrIndex[gridArrIndex.length - 1] = rowIndex;
  const path = getDataPath(entityName, gridArrIndex, entityDefinition);
  const row = _.get(data.entityDataStore.entityData, path);

  const excludedColumnList = excludedColumnsMappingList.map((item) => item.updateTo);
  const parsedExcludedColumnList = excludedColumnList.map((column) =>
    column.includes('.') ? _.last(column.split('.')) : column
  );

  const newRowData = cloneAndOmitDeep(row, [
    ...COPY_ROW_SKIP_ATTRIBUTES,
    ...parsedExcludedColumnList,
  ]);

  dispatch(
    addRow({
      entityName,
      index: gridIndex,
      initialRowData: { ...processCopiedRow(newRowData), _newRowId: newRowId },
      formKey,
      isParentlessTable,
    })
  );
};

export const initializeGridFromDatasource = ({ selectorProps, dataSource, formKey }) => async (
  dispatch,
  getState
) => {
  const { formDesigner } = getState();

  if (!formDesigner[formKey]) {
    return;
  }

  const { entityDefinition, formModel } = formDesigner[formKey];
  const { id, index, entityName, isParentlessTable } = selectorProps;
  const { dataSourceFilter, dataMappingList } = formModel[id];
  const entityType = Object.keys(entityDefinition)[0];
  const newRows = [];

  const values = await dispatch(
    fetchDataSource({
      index,
      dataSource,
      dataSourceFilterFromDataSource: dataSourceFilter, // use control filter to initialize a grid from datasource
      formKey,
      entityName,
      isEditor: false,
      isGridInitialization: true,
      disableCache: true,
    })
  );

  if (!values.length) {
    return;
  }

  dataMappingList.forEach((dataMapping) => {
    const { updateFrom, updateTo } = dataMapping;

    values.forEach((row, i) => {
      const srcValue = row[updateFrom];
      const [targetEntityName, targetFieldName] = updateTo.split('.');
      const castedSrcValue = getValueWithDataType(
        srcValue,
        entityDefinition,
        entityType,
        targetEntityName,
        targetFieldName
      );

      !newRows[i]
        ? (newRows[i] = { [targetFieldName]: castedSrcValue })
        : (newRows[i][targetFieldName] = castedSrcValue);
    });
  });

  batch(() => {
    dispatch(bulkAddingNewRows({ entityName, index, isParentlessTable, newRows, formKey }));
    dispatch(setShouldSkipFormRulesEvaluation(true, formKey));
  });
};

export const initializeHighVolumeGrid = ({ index, formKey, entityName }) => async (
  dispatch,
  getState
) => {
  const { entityDefinition, formInfo, workflowInfo, data, originalData } = getState().formDesigner[
    formKey
  ];

  const path = getDataPath(entityName, index, entityDefinition).slice(0, -1);

  const gridData = _.get(data?.entityDataStore?.entityData, path);
  const gridDataOriginal = _.get(originalData?.entityDataStore?.entityData, path);

  if (_.isNil(gridData) && _.isNil(gridDataOriginal)) {
    const [entityDataType, gridEntitySuffix] = entityName.split('-');

    const response = await getHighVolumeData({
      entityName: formInfo.formEntityKey,
      recordId: workflowInfo.itmId,
      complexAttributeName: entityName,
      filter: [],
      sortBy: [],
    });

    const highVolumeData = preprocessEntityData(
      {
        entityDataStore: {
          entityData: {
            [entityDataType]: {
              [entityName]: _.get(
                response,
                `highVolumeDataStore.entityData.${gridEntitySuffix}`,
                []
              ),
            },
          },
        },
      },
      entityDefinition,
      entityDataType
    );

    const highVolumeDataValue =
      highVolumeData.entityDataStore.entityData[entityDataType][entityName];

    // use the latest data, since it can be changed while fetching highVolumeData
    const { data: latestData } = getState().formDesigner[formKey];
    const newEntityData = set(latestData.entityDataStore.entityData, path, highVolumeDataValue);

    batch(() => {
      if (formKey !== FORM_KEYS.DETAIL) {
        const newOriginalEntityData = set(
          originalData.entityDataStore.entityData,
          path,
          highVolumeDataValue
        );

        const newOriginalData = {
          ...originalData,
          entityDataStore: {
            ...originalData.entityDataStore,
            entityData: newOriginalEntityData,
          },
        };

        dispatch(updateOriginalData(_.cloneDeep(newOriginalData), formKey));
      }

      const newData = {
        ...data,
        entityDataStore: {
          ...data.entityDataStore,
          entityData: newEntityData,
        },
      };

      dispatch(updateDataSuccess(_.cloneDeep(newData), formKey));
    });
  }
};

export const submitForm = ({
  formSubmitType,
  commandName,
  commandArgument,
  needSignature,
  formKey,
  causesValidation,
  meaning,
  pathname,
  operationDelayMessage,
}) => {
  return (dispatch, getState) => {
    const {
      formDesigner: {
        [formKey]: { data, formInfo },
      },
      warnings = [],
    } = getState();

    const hasLeftPanelWithErrors = checkForLeftPanelWithErrors(pathname);
    const setOpenErrorsTab = getSetOpenErrorsTabAction(pathname);

    if (
      (BUTTONS_CAUSE_VALIDATION.includes(formSubmitType) || isTrue(causesValidation)) &&
      warnings.length > 0
    ) {
      batch(() => {
        dispatch(startAutoChecking());
        hasLeftPanelWithErrors && dispatch(setOpenErrorsTab(true));
        isCertificationPage(pathname) && dispatch(setSubmitInitiated(true));
      });
      return;
    }

    dispatch(stopAutoChecking());
    hasLeftPanelWithErrors && dispatch(setOpenErrorsTab(false));

    let requiresSignature;

    if (
      ['true', 'false'].includes(data.formInfo?.requiredESignature) &&
      formSubmitType === 'sign-off'
    ) {
      requiresSignature = isTrue(data.formInfo.requiredESignature);
    } else {
      requiresSignature = needSignature;
    }

    const isMasterSetup = formInfo.formKey.includes('MASTERSETUP');

    if (operationDelayMessage) {
      dispatch(setCustomLongLoadPageMessage(operationDelayMessage));
    }

    if (isMasterSetup) {
      dispatch(clearSuccesses());
      dispatch(clearErrors());
      dispatch(saveRecord(commandName, commandArgument));
    } else {
      startTimer(SUBMIT_TIMER);
      batch(() => {
        dispatch(clearSuccesses());
        dispatch(clearErrors());

        dispatch({
          type: ON_SUBMIT_START,
          formSubmitType,
          commandName,
          commandArgument,
          requiresSignature,
          formKey,
          meaning,
        });
      });
    }
  };
};

export const cancelWorkflowStep = (storeKey) => {
  return (dispatch, getState) => {
    const { taskStatusPanel } = getState();

    const panelConfig = taskStatusPanel.disabled
      ? PANEL_CONFIGURATIONS.ONLY_RIGHT
      : PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED;

    batch(() => {
      clearValidationStorage();
      dispatch(clearFormDesigner(storeKey));
      dispatch(stopAutoChecking());
      dispatch(clearFormErrorsAndWarnings());
      dispatch(setPanelLayoutConfig(panelConfig));
      dispatch(lockPanelLayoutConfig());
      dispatch(setOpenDetailFormTabEntityDisplay(true));
    });
  };
};

export const setUMI = (formKey, flag) => ({
  type: SET_USER_MADE_INTERACTION,
  formKey,
  payload: {
    flag,
  },
});

export const setUserMadeInteraction = (formKey, forceFlag) => {
  return (dispatch, getState) => {
    if (forceFlag !== undefined) {
      dispatch(setUMI(formKey, forceFlag));
      return;
    }

    const userMadeInteraction = getUserMadeInteraction(getState(), { formKey });

    if (!userMadeInteraction) {
      dispatch(setUMI(formKey, true));
    }
  };
};

export const applyFormLevelAddRowRule = ({ addRuleLogInfo, index, entityName, formKey }) => (
  dispatch
) => {
  logInteractionRulesEvaluationResult({ ...addRuleLogInfo, index });
  dispatch(addRow({ entityName, index, initialRowData: {}, formKey }));
};

export const applyFormLevelClearTableRule = ({
  clearTableLogInfo,
  rows,
  index,
  entityName,
  formKey,
}) => (dispatch, getState) => {
  logInteractionRulesEvaluationResult({ ...clearTableLogInfo, index });

  if (clearTableLogInfo.parameterType === 'table') {
    dispatch(clearTable(entityName, index, [], formKey));
    return;
  }

  const { entityDefinition, data, primaryKeysMap } = getState().formDesigner[formKey];
  const primaryKey = primaryKeysMap[entityName];

  const path = getDataPath(entityName, index, entityDefinition);
  const entityDataRows = _.get(data.entityDataStore?.entityData, path.slice(0, -1), []);

  const filteredRowsKeys = rows.filter(({ isFilteredOut }) => isFilteredOut).map(({ key }) => key);

  const clearedRows = entityDataRows.filter((row) => {
    const rowKey = row[primaryKey] ?? row._newRowId;
    return filteredRowsKeys.includes(rowKey);
  });

  dispatch(clearTable(entityName, index, clearedRows, formKey));
};

export const applyAddRowRule = ({ actionDetails, conditions, formKey, index, ruleId }) => (
  dispatch,
  getState
) => {
  const { dummyParameter, parameterType, parameter, type, id, conditionMet } = actionDetails;
  const { formModel } = getState().formDesigner[formKey];
  const formModelControls = Object.values(formModel);
  const controlForInteraction = formModelControls.find(
    ({ uniqueIdentifier }) => uniqueIdentifier === dummyParameter
  );

  const entityName = parameterType === 'control' ? controlForInteraction?.entityName : parameter;
  const ssContext = getSsContext(getState(), { index, formKey, entityName });

  const ruleConditionMet = evaluateConditions({
    conditions,
    ssContext,
  });

  const runAction = conditionMet === false ? !ruleConditionMet : ruleConditionMet;

  if (runAction) {
    const logInfo = {
      actionType: type,
      parameterType,
      targetTable: parameterType === 'control' ? controlForInteraction.uniqueIdentifier : parameter,
      index,
      actionId: id,
      ruleId,
    };

    logInteractionRulesEvaluationResult(logInfo);
    dispatch(addRow({ entityName, index, initialRowData: {}, formKey }));
  }
};

export const applyClearTableRule = ({ actionDetails, conditions, formKey, index, ruleId }) => (
  dispatch,
  getState
) => {
  const { dummyParameter, parameterType, parameter, type, id, conditionMet } = actionDetails;
  const { formModel, selectedSubGrids = [] } = getState().formDesigner[formKey];
  const formModelControls = Object.values(formModel);
  const controlForInteraction = formModelControls.find(
    ({ uniqueIdentifier }) => uniqueIdentifier === dummyParameter
  );

  if (!controlForInteraction && parameterType === 'control') {
    console.log(`📦 Parameter type is "control", but no control found with "${dummyParameter}" id`);
    return;
  }

  const entityName = parameterType === 'control' ? controlForInteraction.entityName : parameter;
  const ssContext = getSsContext(getState(), { index, formKey, entityName });

  const clearedRows =
    parameterType === 'control'
      ? clearTableByUniqueIdentifier({
          control: controlForInteraction,
          entityName,
          ssContext,
        })
      : [];

  const ruleConditionMet = evaluateConditions({
    conditions,
    ssContext,
  });

  const runAction = conditionMet === false ? !ruleConditionMet : ruleConditionMet;

  if (runAction) {
    const logInfo = {
      actionType: type,
      parameterType,
      targetTable: parameterType === 'control' ? controlForInteraction.uniqueIdentifier : parameter,
      index,
      actionId: id,
      ruleId,
    };

    logInteractionRulesEvaluationResult(logInfo);

    batch(() => {
      dispatch(clearTable(entityName, index, clearedRows, formKey));

      const shouldClearSelectedSubGrids = selectedSubGrids.some(
        (subGrid) => subGrid.entityName === entityName
      );

      if (shouldClearSelectedSubGrids) {
        const filteredSelectedSubGrids = selectedSubGrids.filter(
          (subGrid) => !subGrid.entityName.includes(entityName)
        );

        dispatch(updateSelectedSubGrids(filteredSelectedSubGrids, formKey));
      }
    });
  }
};

export const applyRebindCanvasRule = ({ actionDetails, conditions, formKey, index, ruleId }) => (
  dispatch,
  getState
) => {
  const { dummyParameter, parameterType, parameter, type, id, conditionMet } = actionDetails;
  const { formModel } = getState().formDesigner[formKey];
  const formModelControls = Object.values(formModel);
  const targetsIds = [];
  let controlForInteraction = {};

  if (parameterType === 'control') {
    const control = formModelControls.find(
      ({ uniqueIdentifier }) => uniqueIdentifier === dummyParameter
    );

    if (control) {
      controlForInteraction = control;
      targetsIds.push(control.controlId);
    }
  } else {
    const controls = formModelControls.filter(
      ({ entityName, controlType }) =>
        entityName === parameter &&
        [SS_CONTROL_TYPE.GRID, SS_CONTROL_TYPE.REPEATING_PANEL].includes(controlType)
    );

    targetsIds.push(...controls.map(({ controlId }) => controlId));
  }

  const entityName = parameterType === 'control' ? controlForInteraction.entityName : parameter;
  const ssContext = getSsContext(getState(), { index, formKey, entityName });

  const ruleConditionMet = evaluateConditions({
    conditions,
    ssContext,
  });

  const runAction = conditionMet === false ? !ruleConditionMet : ruleConditionMet;

  if (runAction && targetsIds.length) {
    let newFormModel = formModel;

    targetsIds.forEach((id) => {
      const newId = uuidv4();
      newFormModel = changeModelId(newFormModel, id, newId);
    });

    const logInfo = {
      actionType: type,
      parameterType,
      targetTable: parameterType === 'control' ? controlForInteraction.uniqueIdentifier : parameter,
      index,
      actionId: id,
      ruleId,
    };

    logInteractionRulesEvaluationResult(logInfo);

    // REBIND_CANVAS basically rebuilds table
    // the easiest way to do that is to change grid / repeating panel key in formModel, so React will remount the control
    dispatch(updateFormModel(formKey, newFormModel));
  }
};

export const runInteractionRules = ({ index, interaction, formKey }) => (dispatch) => {
  const rulesWithSupportedActions = interaction.rules.filter(({ actions }) =>
    actions.some(({ type }) => INTERACTION_RULES_SUPPORTED_TYPES.includes(type))
  );

  rulesWithSupportedActions.forEach(({ actions, Conditions: conditions, id }) => {
    actions.forEach((action) => {
      const actionArgs = {
        actionDetails: action,
        conditions,
        formKey,
        index,
        ruleId: id,
      };

      switch (action.type) {
        case 'ADD_ROW': {
          dispatch(applyAddRowRule(actionArgs));
          return;
        }

        case 'CLEAR_TABLE': {
          dispatch(applyClearTableRule(actionArgs));
          return;
        }

        case 'REBIND_CANVAS': {
          dispatch(applyRebindCanvasRule(actionArgs));
          return;
        }

        default:
      }
    });
  });
};

export const validateForm = (formKey) => (dispatch, getState) => {
  const state = getState();

  if (!state.formDesigner[formKey]) {
    return;
  }

  const { formModel } = state.formDesigner[formKey];

  const controlsWarnings = validateControls({
    formKey,
    reduxState: state,
  });

  const ssContext = getSsContext(state, { formKey });
  const controlsWithValidation = Object.values(formModel).filter(
    ({ validation }) => !_.isNil(validation)
  );
  const validationRulesWarnings = [];

  controlsWithValidation.forEach((control) => {
    runValidationRules({ control, ssContext, validationRulesWarnings });
  });

  const warnings = [...controlsWarnings, ...validationRulesWarnings];

  dispatch(updateAllWarnings(warnings));
};

export const setActiveContainers = (activeContainers, formKey) => ({
  type: SET_ACTIVE_CONTAINERS,
  formKey,
  payload: {
    activeContainers,
  },
});

export const checkActiveContainers = (formKey) => (dispatch) => {
  if (!RUNTIME_FORMS.includes(formKey)) {
    return;
  }

  const timeout = process.env.NODE_ENV === 'test' ? 0 : 200;

  setTimeout(() => {
    const activeSubGridNodes = document.querySelectorAll('[data-subgrid-expanded="true"]');
    const activeTabNodes = document.querySelectorAll('[data-tab-selected="true"]');

    const activeContainers = {
      activeSubGrids: [...activeSubGridNodes].map((node) => node.id),
      activeTabs: [...activeTabNodes].map((node) => node.id),
    };

    dispatch(setActiveContainers(activeContainers, formKey));
  }, timeout);
};

export const updateSelectedSubGrids = (newSelectedSubGrids, formKey) => ({
  type: UPDATE_SELECTED_SUB_GRIDS,
  formKey,
  payload: { newSelectedSubGrids },
});

export const clearSelectedSubGridsOnOtherForm = (formKey) => (dispatch, getState) => {
  const selectedSubGridsStep = getSelectedSubGrids(getState(), { formKey: FORM_KEYS.STEP });
  const selectedSubGridsDetal = getSelectedSubGrids(getState(), { formKey: FORM_KEYS.DETAIL });

  if (formKey === FORM_KEYS.DETAIL && selectedSubGridsStep.length) {
    dispatch(updateSelectedSubGrids([], FORM_KEYS.STEP));
  }

  if (formKey === FORM_KEYS.STEP && selectedSubGridsDetal.length) {
    dispatch(updateSelectedSubGrids([], FORM_KEYS.DETAIL));
  }
};

export const setSelectedSubGrids = (newSelectedSubGrid, formKey) => (dispatch, getState) => {
  const selectedSubGrids = getSelectedSubGrids(getState(), { formKey });
  const newSelectedSubGrids = handleNewSelectedSubGrid(newSelectedSubGrid, selectedSubGrids);

  batch(() => {
    dispatch(clearSelectedSubGridsOnOtherForm(formKey));
    dispatch(updateSelectedSubGrids(newSelectedSubGrids, formKey));
  });
};

export const removeSelectedSubGrid = (selectedSubGridRowId, formKey) => (dispatch, getState) => {
  const selectedSubGrids = getSelectedSubGrids(getState(), { formKey });
  const selectedSubGridIndex = selectedSubGrids.findIndex(
    ({ rowId }) => rowId === selectedSubGridRowId
  );
  const newSelectedSubGrids = selectedSubGrids.slice(0, selectedSubGridIndex);

  dispatch(updateSelectedSubGrids(newSelectedSubGrids, formKey));
};

export const handleTabChangeForSelectedSubGrids = (formKey) => (dispatch, getState) => {
  const { formDesigner } = getState();

  if (!formDesigner[formKey]) {
    return;
  }

  const { selectedSubGrids = [], formModel } = formDesigner[formKey];

  if (!selectedSubGrids.length) {
    return;
  }

  const activeTabNodes = document.querySelectorAll('[data-tab-selected="true"]');
  const activeTabs = [...activeTabNodes].map((node) => node.id);

  const filteredSelectedSubGrids = selectedSubGrids.filter((subGrid) => {
    let parentId = formModel[subGrid.subgridId].parent;

    while (parentId !== 'root') {
      parentId = formModel[parentId].parent;

      if (formModel[parentId]?.controlType === SS_CONTROL_TYPE.SINGLE_TAB) {
        // eslint-disable-next-line
        const isParentTabActive = activeTabs.some((activeTab) =>
          activeTab.includes(formModel[parentId].controlId)
        );

        if (!isParentTabActive) {
          return false;
        }
      }
    }

    return true;
  });

  if (selectedSubGrids.length !== filteredSelectedSubGrids.length) {
    dispatch(updateSelectedSubGrids(filteredSelectedSubGrids, formKey));
  }
};

export const updateEntityRows = ({ newRows, entityName, index, formKey }) => ({
  type: UPDATE_ENTITY_ROWS,
  formKey,
  payload: {
    newRows,
    entityName,
    index,
  },
});

export const updateMultiSelectDropDownValues = ({
  entityName,
  fieldName,
  formKey,
  index,
  selectedValues,
}) => (dispatch, getState) => {
  const { entityDefinition, data } = getState().formDesigner[formKey];

  const path = getDataPath(entityName, index, entityDefinition);
  const currentRows = _.get(data.entityDataStore.entityData, path.slice(0, -1), []);
  let newRows = [];

  if (selectedValues.length > currentRows.length) {
    const newSelectedValue = selectedValues.find(
      (value) => !currentRows.some((row) => row[fieldName] === value)
    );

    newRows = [...currentRows, { [fieldName]: newSelectedValue }];
  } else {
    newRows = currentRows.filter((row) => selectedValues.includes(row[fieldName]));
  }

  dispatch(updateEntityRows({ newRows, entityName, index, formKey }));
};

export const setIsFormChangesMode = (flag, formKey) => ({
  type: SET_IS_FORM_CHANGES_MODE,
  formKey,
  payload: {
    flag,
  },
});

export const setFormChangesTableRows = (rows, formKey) => ({
  type: SET_FORM_CHANGES_TABLE_ROWS,
  formKey,
  payload: {
    rows,
  },
});

export const setIsFormChangesTableLoading = (flag, formKey) => ({
  type: SET_IS_FORM_CHANGES_TABLE_LOADING,
  formKey,
  payload: {
    flag,
  },
});

export const handleFormChangesMode = (flag, formKey) => async (dispatch, getState) => {
  const {
    formModel,
    formChangesTableRows,
    layoutChanges = {},
    deltaChanges = {},
    formInfo,
  } = getState().formDesigner[formKey];

  const { AddedFields = [], DeletedFields = [], MovedFields = [] } = layoutChanges;
  const { controls = [] } = deltaChanges;
  let pilgrimFormModel = null;

  batch(() => {
    dispatch(setIsFormChangesMode(flag, formKey));

    if (!formChangesTableRows && DeletedFields.length) {
      dispatch(setIsFormChangesTableLoading(true, formKey));
    }
  });

  if (formChangesTableRows) {
    return;
  }

  // we want to show only parent deleted / moved field
  const reducedMovedFields = getReducedFields(MovedFields, formModel);
  let reducedDeletedFields = DeletedFields;

  if (DeletedFields.length) {
    try {
      pilgrimFormModel = await getPilgrimFormModel(formInfo);

      if (pilgrimFormModel) {
        reducedDeletedFields = getReducedFields(DeletedFields, pilgrimFormModel);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const rowsMap = {};
  const formChangesMap = {
    [FORM_CHANGES_TABLE_TYPES.MODIFIED]: controls,
    [FORM_CHANGES_TABLE_TYPES.ADDED]: AddedFields,
    [FORM_CHANGES_TABLE_TYPES.DELETED]: reducedDeletedFields,
    [FORM_CHANGES_TABLE_TYPES.MOVED]: reducedMovedFields,
  };

  for (const key in formChangesMap) {
    rowsMap[key] = formChangesMap[key].map((f, i) => {
      const id = f.controlId || f.ControlId;
      const _formModel = key === FORM_CHANGES_TABLE_TYPES.DELETED ? pilgrimFormModel : formModel;

      let containerFormName = f.containerFormName;
      let formLastModified = f.formLastModified;

      if (key === FORM_CHANGES_TABLE_TYPES.MODIFIED) {
        const parentFormId = findClosestParentIdByType(formModel, id, SS_CONTROL_TYPE.FORM);

        containerFormName = formModel[parentFormId]?.formKey;
        formLastModified = formModel[parentFormId]?.formLastModified;
      }

      return {
        rowId: i,
        id,
        controlType: _formModel?.[id]?.controlType,
        entityName: _formModel?.[id]?.entityName,
        fieldName: _formModel?.[id]?.fieldName,
        containerFormName,
        formLastModified,
      };
    });
  }

  batch(() => {
    dispatch(setFormChangesTableRows(rowsMap, formKey));
    DeletedFields.length && dispatch(setIsFormChangesTableLoading(false, formKey));
  });
};

export const fetchAISummary = ({
  sourceFields,
  showPostSummaryMessage,
  extraPrompts,
  formKey,
  summaryGenerationDate,
  index,
  postSummaryMessage,
  entityName,
}) => async (dispatch, getState) => {
  dispatch(clearWarnings(WARNING_SOURCES.AI_SUMMARY));

  const store = getState();
  const { formDesigner, translations } = store;
  const entityData = formDesigner[formKey]?.data?.entityDataStore?.entityData ?? {};
  const entityDefinition = formDesigner[formKey]?.entityDefinition ?? {};

  const preparedSourceFields = sourceFields.filter(({ field }) => field);

  const filteredEntityData = filterEntityData(
    entityData,
    preparedSourceFields,
    entityDefinition,
    translations,
    store,
    formKey
  );

  const extraPrompt = createExtraPrompt({ extraPrompts, formKey, entityName, index, store });

  const summaryInputData = {
    outputMode: DEFAULT_VIEW_TYPE,
    dataToSummarize: filteredEntityData,
    extraPrompt,
    tokensLimit: 0.6,
  };

  const response = await getAISummary(summaryInputData);

  if (response.hasError) {
    dispatch(handleErrors(response.errors));
    return { hasError: true };
  }

  const shouldUpdateAIDraftedDate =
    summaryGenerationDate && summaryGenerationDate.startsWith('eval||');

  if (shouldUpdateAIDraftedDate) {
    const fieldPath = summaryGenerationDate.split('eval||')[1].split('|')[2] || '';
    const [entityName, fieldName] = fieldPath.split('.');

    const ctrValue = new Date();

    const changeToApply = [{ value: ctrValue, entityName, fieldName, index }];

    dispatch(bulkUpdateFormData(changeToApply, formKey));
  }

  if (showPostSummaryMessage && postSummaryMessage) {
    dispatch(
      addToast({
        message: postSummaryMessage,
        type: TOAST_TYPE.AI,
      })
    );
  }

  const summary = response.summary || '';
  return { summary: summary.trim() };
};

export const evaluateSetValuePropForControls = (formKey) => (dispatch, getState) => {
  const state = getState();

  if (formKey === FORM_KEYS.EDITOR || !state.formDesigner[formKey]) {
    return;
  }

  const { formModel, data, entityDefinition } = state.formDesigner[formKey];
  const entityData = data.entityDataStore?.entityData ?? {};
  const entityType = Object.keys(entityDefinition)[0];

  const controlsWithSetValue = Object.values(formModel).filter(
    (control) => !_.isNil(control.setValue)
  );
  const controlsWithIndexes = getControlsWithIndexes(controlsWithSetValue, entityData, entityType);
  const changesToApply = [];

  controlsWithIndexes.forEach(([{ controlId, entityName, fieldName, setValue }, indexes]) => {
    indexes.forEach((index) => {
      const evaluatedValue = getEvaluatedExpression(state, {
        id: controlId,
        index,
        entityName,
        formKey,
        prop: FORM_MODEL_ATTRIBUTES.SET_VALUE,
      });

      const currentValue = getDataForController({
        entityDefinition,
        entityData,
        entityName,
        fieldName,
        index,
      });

      if (!_.isEqual(evaluatedValue, currentValue)) {
        changesToApply.push({
          value: evaluatedValue,
          entityName,
          fieldName,
          index,
          id: controlId,
          formula: setValue,
        });
      }
    });
  });

  if (changesToApply.length > 0) {
    logSetValueEvaluationResult(changesToApply);
    dispatch(bulkUpdateFormDataWithRules(changesToApply, formKey));
  }
};
